import { scheduledRunsApi } from 'apiConnector/scheduledRun/scheduledRuns';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { merge, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, timeoutWith } from 'rxjs/operators';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import { reloadScheduledRunOccurrenceThunk } from 'state/scheduledRuns/thunks';
import type { EpicWithThunk } from 'state/state.common';
import { HttpTimeoutError, isHttpTimeoutError, type HttpClientError, toTypedError } from 'utils/http/httpClient.model';

export function requestScheduledRunOccurrenceDetailEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (actions$, _, { httpClient }) => {
    const { getOccurrence } = scheduledRunsApi(httpClient);

    return actions$.pipe(
      ofType('SCHEDULED_RUN_OCCURRENCE_DETAIL_REQUESTED'),
      mergeMap(({ id, runId, occurrenceId, requestId }) => {
        const cancellation$ = merge(
          actions$.pipe(
            ofType('SCHEDULED_RUN_OCCURRENCE_RELOAD_RESET'),
            filter(resetAction => resetAction.id === id),
          ),
          actions$.pipe(
            ofType('SCHEDULED_RUN_SELECTED_RUN'),
            filter(selectedRunAction => selectedRunAction.id === id),
          ),
          actions$.pipe(
            ofType('SCHEDULED_RUN_OCCURRENCE_SELECTED'),
            filter(selectedOccurrenceAction => selectedOccurrenceAction.id === id),
          ),
          actions$.pipe(
            ofType('SCHEDULED_RUN_OCCURRENCES_REQUESTED'),
            filter(occurrencesRequestedAction => occurrencesRequestedAction.id === id),
          ),
        );

        return getOccurrence(runId, occurrenceId).pipe(
          timeoutWith(
            timeoutInMillis,
            throwError(() => new HttpTimeoutError(timeoutInMillis)),
            appScheduler,
          ),
          mergeMap(occurrence =>
            of(
              scheduledRunActions.occurrenceDetailReceived(id, runId, occurrenceId, requestId, occurrence),
              reloadScheduledRunOccurrenceThunk(id, requestId),
            ),
          ),
          catchError((error: HttpClientError) =>
            of(
              scheduledRunActions.occurrenceDetailFailed(
                id,
                runId,
                occurrenceId,
                requestId,
                isHttpTimeoutError(error)
                  ? { type: 'technical', subType: 'timeout', source: 'Pricing Runs DIY' }
                  : toTypedError('Pricing Runs DIY', error),
              ),
            ),
          ),
          takeUntil(cancellation$),
        );
      }),
    );
  };
}
